import React, { useState } from 'react';
import { Message as MessageType } from '../../types/kyb';
import ChatInterface from './ChatInterface';

interface AIChatProps {
  initialMessages: MessageType[];
  onMessageSent?: (message: string) => void;
  onSuggestionSelected?: (suggestion: string) => void;
  onRequirementTriggered?: (triggerMessage: string) => void;
}

const AIChat: React.FC<AIChatProps> = ({
  initialMessages,
  onMessageSent,
  onSuggestionSelected,
  onRequirementTriggered
}) => {
  const [messages, setMessages] = useState<MessageType[]>(initialMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [messageIdCounter, setMessageIdCounter] = useState(
    initialMessages.length > 0
      ? Math.max(...initialMessages.map(m => m.id)) + 1
      : 1
  );

  // Simulate AI response
  const simulateResponse = (userMessage: string) => {
    setIsTyping(true);
    
    // In a real app, this would call your backend API
    setTimeout(() => {
      const aiResponse: MessageType = {
        id: messageIdCounter,
        sender: 'ai',
        message: `I've processed your message: "${userMessage}". In a real application, this would be handled by your AI backend.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        suggestions: ['Tell me more', 'Next step']
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setMessageIdCounter(prev => prev + 1);
      setIsTyping(false);
      
      // Check if this message should trigger a requirement
      if (onRequirementTriggered) {
        // In a real app, the backend would determine if a requirement needs to be added
        // For now, we'll just demonstrate the flow with a placeholder
        if (userMessage.toLowerCase().includes('document') || userMessage.toLowerCase().includes('upload')) {
          onRequirementTriggered(aiResponse.message);
        }
      }
    }, 1500);
  };

  const handleSendMessage = (message: string) => {
    const userMessage: MessageType = {
      id: messageIdCounter,
      sender: 'user',
      message,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    setMessages(prev => [...prev, userMessage]);
    setMessageIdCounter(prev => prev + 1);
    
    if (onMessageSent) {
      onMessageSent(message);
    }
    
    simulateResponse(message);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
    
    if (onSuggestionSelected) {
      onSuggestionSelected(suggestion);
    }
  };

  return (
    <ChatInterface
      messages={messages}
      isAITyping={isTyping}
      onSendMessage={handleSendMessage}
      onSuggestionClick={handleSuggestionClick}
    />
  );
};

export default AIChat;